import { useState, useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Textarea } from '@/components/ui'
import { FileText, Hash, Clock, AlignLeft } from 'lucide-react'

export function WordCounter() {
  const [text, setText] = useState('')

  const stats = useMemo(() => {
    const trimmed = text.trim()

    // Characters
    const characters = text.length
    const charactersNoSpaces = text.replace(/\s/g, '').length

    // Words
    const words = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0

    // Sentences (rough estimate)
    const sentences = trimmed ? (trimmed.match(/[.!?]+/g) || []).length : 0

    // Paragraphs
    const paragraphs = trimmed ? trimmed.split(/\n\n+/).filter(Boolean).length : 0

    // Lines
    const lines = text ? text.split('\n').length : 0

    // Reading time (avg 200 words per minute)
    const readingTime = Math.ceil(words / 200)

    // Speaking time (avg 150 words per minute)
    const speakingTime = Math.ceil(words / 150)

    // Unique words
    const wordList = trimmed.toLowerCase().match(/\b[a-z]+\b/g) || []
    const uniqueWords = new Set(wordList).size

    // Average word length
    const avgWordLength = wordList.length > 0
      ? (wordList.reduce((sum, w) => sum + w.length, 0) / wordList.length).toFixed(1)
      : '0'

    // Most common words
    const wordFreq: Record<string, number> = {}
    wordList.forEach((word) => {
      wordFreq[word] = (wordFreq[word] || 0) + 1
    })
    const topWords = Object.entries(wordFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)

    return {
      characters,
      charactersNoSpaces,
      words,
      sentences,
      paragraphs,
      lines,
      readingTime,
      speakingTime,
      uniqueWords,
      avgWordLength,
      topWords,
    }
  }, [text])

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Word Counter</h1>
            <p className="text-muted-foreground">Count words, characters, and analyze text</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{stats.words}</p>
            <p className="text-sm text-muted-foreground">Words</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{stats.characters}</p>
            <p className="text-sm text-muted-foreground">Characters</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{stats.sentences}</p>
            <p className="text-sm text-muted-foreground">Sentences</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{stats.paragraphs}</p>
            <p className="text-sm text-muted-foreground">Paragraphs</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_350px]">
        {/* Text Input */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Text Input</CardTitle>
            <CardDescription>Type or paste your text below</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Start typing or paste your text here..."
              className="min-h-[400px] text-base leading-relaxed"
            />
          </CardContent>
        </Card>

        {/* Detailed Stats */}
        <div className="space-y-6">
          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-primary" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Characters (no spaces)</span>
                <span className="font-mono">{stats.charactersNoSpaces}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lines</span>
                <span className="font-mono">{stats.lines}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unique words</span>
                <span className="font-mono">{stats.uniqueWords}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Avg word length</span>
                <span className="font-mono">{stats.avgWordLength}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Reading Time
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reading</span>
                <span className="font-mono">{stats.readingTime} min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Speaking</span>
                <span className="font-mono">{stats.speakingTime} min</span>
              </div>
            </CardContent>
          </Card>

          {stats.topWords.length > 0 && (
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlignLeft className="h-5 w-5 text-primary" />
                  Top Words
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.topWords.map(([word, count], i) => (
                    <div key={word} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                      <span className="flex-1 font-mono">{word}</span>
                      <span className="text-sm text-primary">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
